import {
  KeyboardEvent,
  Ref,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { InputText } from 'primereact/inputtext';
import { ListBox } from 'primereact/listbox';
import {
  BmweColumnMeta,
  SelectFilterOption,
  TextFilterValue,
} from '../../../types';
import { EMPTY_FILTER_VALUE, markUsedFilterOptions } from '../../../helper';
import { useDebounce } from '../../../hooks/useDebounce';
import { tableStrings } from '../i18n/de-de';
import tableStyles from '../bmweDataTable.module.css';
import styles from './filterPin.module.css';

const EMPTY_OPTION: SelectFilterOption = {
  label: tableStrings.filter.emptyOptionLabel,
  value: EMPTY_FILTER_VALUE,
};

interface ValueTabsFilterProps {
  /** `select`: pick from the column's options only; `text`: also add own texts (matched by "contains"). */
  kind: 'select' | 'text';
  meta?: BmweColumnMeta;
  value: TextFilterValue;
  onChange: (value: TextFilterValue) => void;
  columnName: string;
  ref?: Ref<ValueTabsFilterHandle>;
}

export interface ValueTabsFilterHandle {
  /** Called by the popup for clicks inside it; a click outside the input and its list ends "creating new". */
  handlePanelClick: (target: Node) => void;
}

/** A "+" that turns into the search input (open from the start without chips) on top, the chosen values as removable chips below it, then the matching values. */
export function ValueTabsFilter({
  kind,
  meta,
  value,
  onChange,
  columnName,
  ref,
}: ValueTabsFilterProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const addAreaRef = useRef<HTMLDivElement>(null);
  const [adding, setAdding] = useState(
    !value.values.length && !value.texts.length,
  );
  const [search, setSearch] = useState('');
  const trimmed = search.trim();
  const distinctValues = kind === 'text' ? meta?.distinctValues : undefined;
  const debouncedSearch = useDebounce(trimmed, 300);

  const { data: remote, isFetching } = useQuery({
    queryKey: [...(distinctValues?.queryKey ?? []), debouncedSearch],
    queryFn: () =>
      distinctValues?.fetch(debouncedSearch) ??
      Promise.resolve({ values: [], hasMore: false, supported: false }),
    enabled: !!distinctValues && adding,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  // Facets load only while the value list is open; reopening reuses the cache.
  const facet = kind === 'select' ? meta?.filterFacet : undefined;
  const { data: facetValues, isFetching: facetFetching } = useQuery({
    queryKey: facet?.queryKey ?? ['filterFacet'],
    queryFn: () => facet?.fetch() ?? Promise.resolve([]),
    enabled: !!facet && adding,
    staleTime: 60_000,
  });

  const selectOptions = useMemo<SelectFilterOption[]>(() => {
    if (facet?.asOptions)
      return (facetValues ?? []).map((v) => ({ label: v, value: v }));
    return markUsedFilterOptions(meta?.filterOptions ?? [], facetValues);
  }, [facet?.asOptions, facetValues, meta?.filterOptions]);

  const notNullable = meta?.notNullable;
  const allOptions = useMemo<SelectFilterOption[]>(() => {
    const empty = notNullable ? [] : [EMPTY_OPTION];
    if (kind === 'select') return [...empty, ...selectOptions];
    if (!distinctValues) return [];
    return [
      ...empty,
      ...(remote?.values ?? []).map((v) => ({ label: v, value: v })),
    ];
  }, [kind, notNullable, selectOptions, distinctValues, remote]);

  const listed = useMemo(() => {
    const needle = trimmed.toLocaleLowerCase('de');
    const chosen = new Set(value.values);
    return allOptions.filter(
      (option) =>
        !chosen.has(option.value) &&
        option.label.toLocaleLowerCase('de').includes(needle),
    );
  }, [allOptions, value.values, trimmed]);

  const labelOf = (v: string) =>
    v === EMPTY_FILTER_VALUE
      ? EMPTY_OPTION.label
      : (meta?.filterOptions?.find((o) => o.value === v)?.label ?? v);

  const pick = (v: string) => {
    onChange({ ...value, values: [...value.values, v] });
    setSearch('');
  };
  const addText = () => {
    if (kind !== 'text' || !trimmed || value.texts.includes(trimmed)) return;
    onChange({ ...value, texts: [...value.texts, trimmed] });
    setSearch('');
  };
  const stopAdding = () => {
    setSearch('');
    setAdding(false);
  };
  const isInAddArea = (node: Node | null) =>
    !!node &&
    (!!inputRef.current?.contains(node) ||
      !!addAreaRef.current?.contains(node));

  // The popup forwards its clicks after the chips' and list's own handlers ran;
  // clicks outside the popup close it altogether.
  useImperativeHandle(
    ref,
    () => ({
      handlePanelClick: (target) => {
        if (adding && !isInAddArea(target)) stopAdding();
      },
    }),
    [adding],
  );
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addText();
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      stopAdding();
    }
  };

  const chip = (key: string, label: string, onRemove: () => void) => (
    <Chip
      key={key}
      label={label}
      removable
      className={styles.valueChip}
      onRemove={() => {
        onRemove();
        return true;
      }}
      pt={{
        removeIcon: {
          role: 'button',
          'aria-label': tableStrings.filter.removeValue(label),
        },
      }}
    />
  );

  // The list only shows together with the input opened via "+"; columns the
  // backend has no value list for only offer typed text.
  const showList =
    adding &&
    (kind === 'select' || (!!distinctValues && remote?.supported !== false));
  const loading =
    (!!distinctValues && isFetching && !remote) ||
    (!!facet?.asOptions && facetFetching && !facetValues);
  const hasChips = value.values.length > 0 || value.texts.length > 0;

  return (
    <div className={styles.valueTabs}>
      <div className={styles.searchRow}>
        {adding ? (
          <InputText
            ref={inputRef}
            className={`p-inputtext-sm ${styles.addInput}`}
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              kind === 'text'
                ? tableStrings.filter.searchOrAddPlaceholder
                : tableStrings.filter.searchPlaceholder
            }
            aria-label={tableStrings.messages.filterBy(columnName)}
          />
        ) : (
          <Button
            type="button"
            icon="pi pi-plus"
            outlined
            severity="secondary"
            size="small"
            onClick={() => setAdding(true)}
            aria-label={tableStrings.filter.addValue}
            title={tableStrings.filter.addValue}
          />
        )}
      </div>

      {hasChips && (
        <div className={styles.tabRow}>
          {value.values.map((v) =>
            chip(`v:${v}`, labelOf(v), () =>
              onChange({
                ...value,
                values: value.values.filter((x) => x !== v),
              }),
            ),
          )}
          {value.texts.map((t) =>
            chip(`t:${t}`, t, () =>
              onChange({ ...value, texts: value.texts.filter((x) => x !== t) }),
            ),
          )}
        </div>
      )}

      {adding && (
        <div ref={addAreaRef} className={styles.addArea}>
          {kind === 'text' && trimmed && !value.texts.includes(trimmed) && (
            <Button
              type="button"
              link
              size="small"
              icon="pi pi-plus"
              label={tableStrings.filter.addText(trimmed)}
              className={styles.addText}
              onClick={addText}
            />
          )}

          {showList && (
            <ListBox
              // Keeps focus in the input so a click on an entry doesn't close the list first.
              onMouseDown={(e) => e.preventDefault()}
              options={listed}
              optionLabel="label"
              optionValue="value"
              value={null}
              onChange={(e) => e.value != null && pick(e.value as string)}
              itemTemplate={(option: SelectFilterOption) => (
                <span
                  className={
                    option.unused
                      ? tableStyles.filterOptionUnused
                      : option.unused === false
                        ? tableStyles.filterOptionUsed
                        : undefined
                  }
                >
                  {option.label}
                </span>
              )}
              emptyMessage={
                loading
                  ? tableStrings.filter.optionsLoading
                  : tableStrings.filter.noMoreValues
              }
              aria-label={columnName}
              className={styles.optionList}
              listStyle={{ maxHeight: '16rem' }}
            />
          )}

          {showList && remote?.hasMore && (
            <div className={tableStyles.filterOptionLegend}>
              {tableStrings.filter.moreValuesHint}
            </div>
          )}
          {showList &&
            kind === 'select' &&
            selectOptions.some((o) => o.unused) && (
              <div className={tableStyles.filterOptionLegend}>
                {tableStrings.filter.unusedOptionHint}
              </div>
            )}
        </div>
      )}
    </div>
  );
}
